import React, { useEffect, useState, useCallback } from 'react';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import NewBadge from '../../NewBadge';
import styles from './TimeWidget.module.scss';

// SVG circle parameters
const CIRCLE_SIZE = 160;
const STROKE_WIDTH = 4;
const RADIUS = (CIRCLE_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export interface TimeWidgetProps {
  timezone?: string | null;
  showBadge?: boolean;
}

export default function TimeWidget({
  timezone,
  showBadge = false,
}: TimeWidgetProps) {
  const getTimeInTimezone = useCallback((): {
    time: string;
    minutes: number;
    date: Date;
  } => {
    const now = new Date();

    if (timezone) {
      try {
        const timeString = now.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: timezone,
        });

        // Get minutes in the specified timezone
        const minutesString = now.toLocaleTimeString([], {
          minute: '2-digit',
          timeZone: timezone,
        });
        const mins = parseInt(minutesString, 10);

        return { time: timeString, minutes: mins, date: now };
      } catch {
        // Fall back to local time if timezone is invalid
        return {
          time: now.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          minutes: now.getMinutes(),
          date: now,
        };
      }
    }

    return {
      time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      minutes: now.getMinutes(),
      date: now,
    };
  }, [timezone]);

  const [currentTime, setCurrentTime] = useState<string>(
    () => getTimeInTimezone().time,
  );
  const [minutes, setMinutes] = useState<number>(
    () => getTimeInTimezone().minutes,
  );
  const [currentDate] = useState<Date>(() => new Date());

  useEffect(() => {
    const updateTime = () => {
      const { time, minutes: mins } = getTimeInTimezone();
      setCurrentTime(time);
      setMinutes(mins);
    };

    // Update immediately when timezone changes
    updateTime();

    const intervalId = setInterval(updateTime, 1000);

    return () => clearInterval(intervalId);
  }, [getTimeInTimezone]);

  // Calculate progress (0-1) based on minutes (0-59)
  const progress = minutes / 60;
  const strokeDashoffset = CIRCUMFERENCE * (1 - progress);

  return (
    <div className={styles.container}>
      {showBadge && <NewBadge className={styles.badge} />}
      <div className={styles.circleWrapper}>
        <div className={styles.backgroundCircle} />
        <svg
          className={styles.progressRing}
          width={CIRCLE_SIZE}
          height={CIRCLE_SIZE}
          viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}
        >
          {/* Background ring (subtle) */}
          <circle
            className={styles.progressRingBackground}
            cx={CIRCLE_SIZE / 2}
            cy={CIRCLE_SIZE / 2}
            r={RADIUS}
            fill="none"
            strokeWidth={STROKE_WIDTH}
          />
          {/* Progress arc (white) */}
          <circle
            className={styles.progressRingProgress}
            cx={CIRCLE_SIZE / 2}
            cy={CIRCLE_SIZE / 2}
            r={RADIUS}
            fill="none"
            strokeWidth={STROKE_WIDTH}
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </svg>
        <div className={styles.content}>
          <p className={styles.time}>{currentTime}</p>
          <p className={styles.date}>{getFormattedDate(currentDate)}</p>
        </div>
      </div>
    </div>
  );
}
